import { useLayoutEffect, useState } from 'react';

import ResizeObserver from 'resize-observer-polyfill';

/**
 * How far below the top of `scrollElement`'s content `marker` sits, kept current as the content
 * around it changes size. Null until it has been measured on a laid-out scroller: a hidden tab
 * reports zero sizes, which would place every row at the top.
 */
export function useListStartOffset({
  marker,
  scrollElement,
}: {
  marker: HTMLElement | null;
  scrollElement: HTMLElement | null;
}): number | null {
  const [offset, setOffset] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!marker || !scrollElement) return;

    const measure = (): void => {
      if (!scrollElement.clientHeight) return;
      const markerTop = marker.getBoundingClientRect().top;
      const scrollerTop = scrollElement.getBoundingClientRect().top;
      setOffset(Math.round(markerTop - scrollerTop + scrollElement.scrollTop));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scrollElement);
    for (const child of Array.from(scrollElement.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [marker, scrollElement]);

  return offset;
}
